/* eslint-disable no-underscore-dangle */

import { isActuatingGripper } from '@sb/integrations/utils';
import { FailureKind } from '@sb/routine-runner';
import type { StepPlayArguments } from '@sb/routine-runner/Step/Step';
import Step from '@sb/routine-runner/Step/Step';
import { wait } from '@sb/utilities';

import { EnableGripperArguments as Arguments } from './Arguments';
import { EnableGripperVariables as Variables } from './Variables';

export class EnableGripperStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {
      succeeded: false,
    };
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    const { payload, deviceID, waitForGripperToInitialize } = this.args;

    this.setVariable('succeeded', false);

    if (!deviceID) {
      return fail({
        failure: {
          kind: FailureKind.GripperFailure,
          message: `No device ID provided.`,
        },
        failureReason: `No device ID provided.`,
      });
    }

    const equipmentState = this.routineContext.equipment.getEquipmentState();

    const enabledGripper = equipmentState.find((item) =>
      isActuatingGripper(item.kind),
    );

    if (enabledGripper != null) {
      if (enabledGripper.id !== deviceID) {
        return fail({
          failure: {
            kind: FailureKind.GripperFailure,
            message: `An actuating gripper is already enabled.`,
          },
          failureReason: `An actuating gripper is already enabled.`,
        });
      }
    } else {
      try {
        await this.routineContext.disabledEquipment.enableEquipment(deviceID);
        // We wait for 500ms to ensure the gripper state has time to update
        await wait(500);
      } catch (error) {
        return fail({
          failure: {
            kind: FailureKind.GripperFailure,
            message: `Failed to enable gripper: ${error.message}`,
          },
          failureReason: `Failed to enable gripper: ${error.message}`,
          error,
        });
      }
    }

    if (waitForGripperToInitialize) {
      const maxWaitTime = 15000; // 15 seconds
      const pollInterval = 100; // 100ms
      const startTime = Date.now();

      while (Date.now() - startTime < maxWaitTime) {
        if (await this.shouldStopPlaying()) {
          return undefined;
        }

        const currentEquipmentState =
          this.routineContext.equipment.getEquipmentState();

        const newlyEnabledGripper = currentEquipmentState.find(
          (item) => isActuatingGripper(item.kind) && item.id === deviceID,
        );

        if (
          newlyEnabledGripper?.state &&
          typeof newlyEnabledGripper.state === 'object' &&
          'isConnected' in newlyEnabledGripper.state &&
          (newlyEnabledGripper.state as { isConnected?: boolean })
            .isConnected === true
        ) {
          // Gripper is connected, proceed
          break;
        }

        await wait(pollInterval);
      }

      const finalEquipmentState =
        this.routineContext.equipment.getEquipmentState();

      const finalGripper = finalEquipmentState.find(
        (item) => isActuatingGripper(item.kind) && item.id === deviceID,
      );

      if (
        !finalGripper?.state ||
        typeof finalGripper.state !== 'object' ||
        !('isConnected' in finalGripper.state) ||
        (finalGripper.state as { isConnected?: boolean }).isConnected !== true
      ) {
        return fail({
          failure: {
            kind: FailureKind.GripperFailure,
            message: `Gripper did not connect within the timeout period.`,
          },
          failureReason: `Gripper did not connect within the timeout period.`,
        });
      }
    }

    if (payload) {
      await this.routineContext.setPayload(payload);
    }

    this.setVariable('succeeded', true);

    return undefined;
  }
}
